import React, { useState, useEffect, useCallback } from "react";
import { GlobalTable, HeaderLine, Td, Th, Tr } from "../../../../components/styles/table-style";
import { PrioritySelect, Select } from "./style";
import FashionApiService from '../../../../services/FashionApiService';
import ApiService from '../../../../services/ApiService';

export interface FashionProductionItemsData {
    id: number;
    fashionProductionOrderId: number;
    fashionOrderId: number;
    quantityForThisProduction: number;
    fashionOrder?: {
        id: number;
    };
}

export interface FashionProductionOrderData {
    id: number;
    set: string;
    status?: string;
    dressmakerId?: any;
    cutterId?: any;
    color?: any;
    totalItems?: any;
    fashionProduct?: any;
    priority?: string;
    machineId?: number;
    productionDay?: string;

    fashionProductionItems?: FashionProductionItemsData[];
}

interface Machine {
    id: number;
    code: string;
    name: string;
}

interface TableRowData {
    op: string;
    pedido: string;
    qtdItens: number;
    etapa: string;
    prioridade: string;
    machineId: number | null;
    dia: string;
    originalOrderId: number;
}

const PRIORIDADE_OPTIONS = ['Alta', 'Média', 'Baixa'];
const DIA_OPTIONS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];

const TablePCP: React.FC = () => {
    const columns = ['OP', 'Pedido', 'Qtd. de itens', 'Etapa', 'Prioridade', 'Máquina', 'Dia'];

    const [tableData, setTableData] = useState<TableRowData[]>([]);
    const [machines, setMachines] = useState<Machine[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchMachines = useCallback(async () => {
        try {
            const response = await ApiService.get('/equipment/equipment');
            setMachines(response.data);
        } catch (err: any) {
            console.error("Erro ao buscar máquinas:", err);
            setError("Não foi possível carregar as máquinas.");
        }
    }, []);

    const fetchAndProcessProductionOrders = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await FashionApiService.getProductionOrders();
            const rawOrders: FashionProductionOrderData[] = response.data;

            const formattedData: TableRowData[] = rawOrders.map(order => {
                const opNumber = `OP-${order.id}`;

                const pedidos = order.fashionProductionItems
                    ?.map(item => `PED-${item.fashionOrderId}`)
                    .join(', ');

                const totalItems = order.fashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

                const initialPriority = order.priority && PRIORIDADE_OPTIONS.includes(order.priority) ? order.priority : '';
                const initialMachineId = order.machineId || null;
                const initialDay = order.productionDay && DIA_OPTIONS.includes(order.productionDay) ? order.productionDay : '';


                return {
                    originalOrderId: order.id,
                    op: opNumber,
                    pedido: pedidos || 'N/A',
                    qtdItens: totalItems,
                    etapa: order.status || 'N/A',
                    prioridade: initialPriority,
                    machineId: initialMachineId,
                    dia: initialDay,
                };
            });

            setTableData(formattedData);
        } catch (err: any) {
            console.error("Erro ao buscar ordens de produção:", err);
            setError("Não foi possível carregar as ordens de produção.");
        } finally {
            setIsLoading(false);
        }
    }, [machines]);

    useEffect(() => {
        fetchMachines();
    }, [fetchMachines]);

    useEffect(() => {
        if (machines.length > 0) {
            fetchAndProcessProductionOrders();
        }
    }, [machines, fetchAndProcessProductionOrders]);

    const handleSelectChange = async (
        rowIndex: number,
        field: keyof TableRowData,
        value: string
    ) => {
        const row = tableData[rowIndex];
        const orderId = row.originalOrderId;

        setTableData(prevData => {
            const newData = [...prevData];
            const updatedValue = field === 'machineId' ? (value === '' ? null : Number(value)) : value;
            const updatedRow = { ...newData[rowIndex], [field]: updatedValue };
            newData[rowIndex] = updatedRow;
            return newData;
        });

        const updateData: any = {};

        if (field === 'machineId') {
            updateData.machineId = value === '' ? null : Number(value);
        } else if (field === 'prioridade') {
            updateData.priority = value === '' ? null : value;
        } else if (field === 'dia') {
            updateData.productionDay = value === '' ? null : value;
        }

        try {
            await FashionApiService.updateProductionOrder(orderId, updateData);

            let fieldName = '';
            let newValue = '';

            if (field === 'machineId') {
                const machine = machines.find(m => m.id === Number(value));
                fieldName = 'Máquina';
                newValue = machine ? `${machine.code} - ${machine.name}` : 'Nenhuma';
            } else if (field === 'prioridade') {
                fieldName = 'Prioridade';
                newValue = value || 'Nenhuma';
            } else if (field === 'dia') {
                fieldName = 'Dia de produção';
                newValue = value || 'Nenhum';
            }

            console.log(`✅ SUCESSO: ${fieldName} da OP-${orderId} atualizada para ${newValue}`);
        } catch (err) {
            console.error(`Erro ao atualizar ${field} da OP ${orderId}:`, err);
            alert(`ERRO: Falha ao atualizar ${field} da OP-${orderId}. Verifique o console.`);
            fetchAndProcessProductionOrders();
        }
    };

    if (isLoading) {
        return <p>Carregando ordens de produção...</p>;
    }

    if (error) {
        return <p style={{ color: 'red' }}>Erro: {error}</p>;
    }

    if (tableData.length === 0) {
        return <p>Nenhuma ordem de produção encontrada.</p>;
    }

    return (
        <GlobalTable>
            <thead>
                <HeaderLine>
                    {columns.map((item, index) => (
                        <Th key={index}>{item}</Th>
                    ))}
                </HeaderLine>
            </thead>
            <tbody>
                {tableData.map((rowData, rowIndex) => (
                    <Tr key={rowData.originalOrderId}>
                        <Td>{rowData.op}</Td>
                        <Td>{rowData.pedido}</Td>
                        <Td>{rowData.qtdItens}</Td>
                        <Td>{rowData.etapa}</Td>

                        <Td>
                            <PrioritySelect
                                value={rowData.prioridade}
                                onChange={(e) => handleSelectChange(rowIndex, 'prioridade', e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {PRIORIDADE_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </PrioritySelect>
                        </Td>

                        <Td>
                            <Select
                                value={rowData.machineId || ''}
                                onChange={(e) => handleSelectChange(rowIndex, 'machineId', e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {machines.map(machine => (
                                    <option key={machine.id} value={machine.id}>
                                        {machine.code} - {machine.name}
                                    </option>
                                ))}
                            </Select>
                        </Td>

                        <Td>
                            <Select
                                value={rowData.dia}
                                onChange={(e) => handleSelectChange(rowIndex, 'dia', e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {DIA_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Select>
                        </Td>
                    </Tr>
                ))}
            </tbody>
        </GlobalTable>
    );
};

export default TablePCP;
